/**
 * Filter chain builders for ffmpeg.wasm
 * Builds filter_complex strings for video xfade and audio crossfade
 */

/**
 * Calculate xfade offsets for a chain of crossfades
 * Offset formula: offset[n] = sum(durations[:n+1]) - ((n+1) * transition_duration)
 *
 * @param {number[]} durations - Array of clip durations in seconds
 * @param {number} transitionDuration - Crossfade duration in seconds
 * @returns {number[]} Array of offsets for each transition
 */
export function calculateOffsets(durations, transitionDuration) {
  const offsets = [];
  for (let n = 0; n < durations.length - 1; n++) {
    // Sum of durations up to and including index n
    const cumulative = durations.slice(0, n + 1).reduce((a, b) => a + b, 0);
    // Offset accounts for previous crossfades "eating" time
    const offset = cumulative - ((n + 1) * transitionDuration);
    offsets.push(offset);
  }
  return offsets;
}

/**
 * Build video-only filter_complex string for xfade transitions
 *
 * @param {number} repeatCount - Number of times to repeat the clip
 * @param {number} clipDuration - Duration of the input clip in seconds
 * @param {number} crossfadeDuration - Duration of each crossfade in seconds
 * @returns {string} The video filter_complex string for FFmpeg
 */
export function buildVideoFilterComplex(repeatCount, clipDuration, crossfadeDuration) {
  if (repeatCount < 2) {
    return '[0:v]copy[vout]';
  }

  const durations = Array(repeatCount).fill(clipDuration);
  const offsets = calculateOffsets(durations, crossfadeDuration);

  const filters = [];
  const numTransitions = repeatCount - 1;

  for (let i = 0; i < numTransitions; i++) {
    const input1 = i === 0 ? '[0:v]' : `[v${String(i - 1).padStart(2, '0')}]`;
    const input2 = `[${i + 1}:v]`;
    const output = i === numTransitions - 1 ? '[vout]' : `[v${String(i).padStart(2, '0')}]`;
    const offset = offsets[i].toFixed(3);
    const filter = `${input1}${input2}xfade=transition=fade:duration=${crossfadeDuration}:offset=${offset}${output}`;
    filters.push(filter);
  }

  return filters.join(';');
}

/**
 * Build audio crossfade filter_complex string using acrossfade
 * Uses qsin (quarter-sine) curves for constant-power crossfade
 *
 * @param {number} repeatCount - Number of times to repeat the clip
 * @param {number} crossfadeDuration - Duration of each crossfade in seconds
 * @returns {string} The audio filter_complex string for FFmpeg
 */
export function buildAudioFilterComplex(repeatCount, crossfadeDuration) {
  if (repeatCount < 2) {
    return '[0:a]acopy[aout]';
  }

  const filters = [];
  const numTransitions = repeatCount - 1;

  for (let i = 0; i < numTransitions; i++) {
    const input1 = i === 0 ? '[0:a]' : `[a${String(i - 1).padStart(2, '0')}]`;
    const input2 = `[${i + 1}:a]`;
    const output = i === numTransitions - 1 ? '[aout]' : `[a${String(i).padStart(2, '0')}]`;
    // Using qsin curves for constant-power crossfade (maintains perceived loudness)
    const filter = `${input1}${input2}acrossfade=d=${crossfadeDuration}:c1=qsin:c2=qsin${output}`;
    filters.push(filter);
  }

  return filters.join(';');
}

/**
 * Build audio crossfade filter_complex string using afade fallback
 * For when acrossfade is not available - uses fade-out/fade-in with concat
 *
 * @param {number} repeatCount - Number of times to repeat the clip
 * @param {number} clipDuration - Duration of the input clip in seconds
 * @param {number} crossfadeDuration - Duration of each crossfade in seconds
 * @returns {string} The audio filter_complex string for FFmpeg
 */
export function buildAudioFilterComplexFallback(repeatCount, clipDuration, crossfadeDuration) {
  if (repeatCount < 2) {
    return '[0:a]acopy[aout]';
  }

  const filters = [];

  // For each input, apply fade-out at end and fade-in at start (except first/last)
  for (let i = 0; i < repeatCount; i++) {
    const fadeOutStart = clipDuration - crossfadeDuration;
    let filter = `[${i}:a]`;

    if (i > 0) {
      // Fade in at start (except for first clip)
      filter += `afade=t=in:st=0:d=${crossfadeDuration}`;
    }

    if (i < repeatCount - 1) {
      // Fade out at end (except for last clip)
      if (i > 0) {
        filter += ',';
      }
      filter += `afade=t=out:st=${fadeOutStart.toFixed(3)}:d=${crossfadeDuration}`;
    }

    filter += `[a${i}]`;
    filters.push(filter);
  }

  // Concat all audio streams
  const concatInputs = Array(repeatCount).fill(0).map((_, i) => `[a${i}]`).join('');
  filters.push(`${concatInputs}concat=n=${repeatCount}:v=0:a=1[aout]`);

  return filters.join(';');
}

/**
 * Build combined video+audio filter_complex string
 *
 * @param {number} repeatCount - Number of times to repeat the clip
 * @param {number} clipDuration - Duration of the input clip in seconds
 * @param {number} crossfadeDuration - Duration of each crossfade in seconds
 * @param {boolean} hasAudio - Whether the input has an audio stream
 * @param {boolean} useAcrossfade - Whether to use acrossfade (true) or afade fallback (false)
 * @returns {string} The combined filter_complex string for FFmpeg
 */
export function buildCombinedFilterComplex(repeatCount, clipDuration, crossfadeDuration, hasAudio, useAcrossfade = true) {
  const videoFilter = buildVideoFilterComplex(repeatCount, clipDuration, crossfadeDuration);

  if (!hasAudio) {
    return videoFilter;
  }

  const audioFilter = useAcrossfade
    ? buildAudioFilterComplex(repeatCount, crossfadeDuration)
    : buildAudioFilterComplexFallback(repeatCount, clipDuration, crossfadeDuration);

  return `${videoFilter};${audioFilter}`;
}

/**
 * Calculate expected output duration after crossfades
 *
 * Formula: totalDuration = (clipDuration * repeatCount) - (crossfadeDuration * (repeatCount - 1))
 *
 * @param {number} clipDuration - Duration of the input clip in seconds
 * @param {number} repeatCount - Number of repetitions
 * @param {number} crossfadeDuration - Duration of each crossfade in seconds
 * @returns {number} Expected output duration in seconds
 */
export function calculateExpectedDuration(clipDuration, repeatCount, crossfadeDuration) {
  if (repeatCount <= 1) {
    return clipDuration;
  }
  return (clipDuration * repeatCount) - (crossfadeDuration * (repeatCount - 1));
}
